import { useEffect, useRef, useState } from "react";
import { Loader2, Truck, Play, CheckCircle2, Pause, Phone, Timer, PencilLine, FileCheck2, ChevronRight, Bot, Lock } from "lucide-react";
import { toast } from "sonner";
import { useEmployeeApi } from "../employeeApi";

const ACTION_LABEL = { paa_vej: "På vej", start_arbejde: "Start arbejde", afslut: "Afslut opgave" };
const ACTION_ICON = { paa_vej: Truck, start_arbejde: Play, afslut: CheckCircle2 };

function fmt(secs) {
    secs = Math.max(0, Math.floor(secs || 0));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function Stepper({ steps }) {
    return (
        <ol className="flex items-center" data-testid="workflow-stepper">
            {steps.map((st, i) => {
                const color = st.state === "done" ? "bg-emerald-500 text-white" : st.state === "current" ? "bg-blue-600 text-white ring-4 ring-blue-100" : "bg-slate-200 text-slate-400";
                const textc = st.state === "done" ? "text-emerald-600" : st.state === "current" ? "text-blue-600" : "text-slate-400";
                const linec = steps[i + 1] && (st.state === "done" ? "bg-emerald-500" : "bg-slate-200");
                return (
                    <li key={st.key} className="flex-1 flex flex-col items-center relative" data-testid={`step-${st.key}`} data-state={st.state}>
                        <div className="flex items-center w-full">
                            <div className="flex-1" />
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${color}`}>
                                {st.state === "done" ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                            </span>
                            <div className="flex-1 relative">
                                {steps[i + 1] && <span className={`absolute left-0 right-0 top-1/2 -translate-y-1/2 h-0.5 ${linec}`} />}
                            </div>
                        </div>
                        <span className={`text-[11px] font-semibold mt-1.5 ${textc}`}>{st.label}</span>
                    </li>
                );
            })}
        </ol>
    );
}

export default function WorkflowSection({ jobId, job, wf, onChange, onRefresh, locked = false }) {
    const api = useEmployeeApi();
    const [busy, setBusy] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [editTime, setEditTime] = useState(false);
    const [driveMin, setDriveMin] = useState(0);
    const [workMin, setWorkMin] = useState(0);
    // Live-ticking mirror of server seconds.
    const [drive, setDrive] = useState(wf.drive_secs || 0);
    const [work, setWork] = useState(wf.work_secs || 0);
    const tick = useRef(null);

    useEffect(() => { setDrive(wf.drive_secs || 0); setWork(wf.work_secs || 0); }, [wf.drive_secs, wf.work_secs, wf.stage, wf.paused]);
    useEffect(() => {
        clearInterval(tick.current);
        if (wf.driving || wf.working) {
            tick.current = setInterval(() => {
                if (wf.driving) setDrive((d) => d + 1);
                if (wf.working) setWork((w) => w + 1);
            }, 1000);
        }
        return () => clearInterval(tick.current);
    }, [wf.driving, wf.working]);

    const apply = (res) => { onChange(res.workflow, res.status); };

    const doAdvance = async (action) => {
        setBusy(action);
        try { apply(await api.advanceWorkflow(jobId, action)); onRefresh?.(); toast.success(action === "afslut" ? "Opgave afsluttet — fakturaklar" : `Forløb: ${ACTION_LABEL[action]}`); }
        catch (e) { toast.error(e?.response?.data?.detail || "Handlingen kunne ikke gennemføres"); }
        finally { setBusy(""); setConfirming(false); }
    };

    const togglePause = async () => {
        setBusy("pause");
        try { apply(await api.pauseWorkflow(jobId, !wf.paused)); }
        catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke ændre pause"); }
        finally { setBusy(""); }
    };

    const saveTime = async () => {
        setBusy("time");
        try {
            apply(await api.correctTime(jobId, { work_secs: Math.round(workMin * 60), drive_secs: Math.round(driveMin * 60) }));
            toast.success("Tid rettet"); setEditTime(false);
        } catch { toast.error("Kunne ikke rette tiden"); }
        finally { setBusy(""); }
    };

    const next = wf.next_action;
    const NextIcon = next ? ACTION_ICON[next] : null;
    const showTimes = wf.stage !== "modtaget";
    const takeStartLocked = locked && (next === "paa_vej" || next === "start_arbejde");

    return (
        <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm space-y-4" data-testid="workflow-section">
            <Stepper steps={wf.steps} />

            {/* Primary next action */}
            {takeStartLocked ? (
                <div className="w-full h-12 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 font-bold flex items-center justify-center gap-2" data-testid="workflow-locked">
                    <Lock className="w-5 h-5" /> Afventer vare
                </div>
            ) : next ? (
                confirming && next === "afslut" ? (
                    <div className="space-y-2" data-testid="workflow-confirm">
                        <p className="text-sm text-slate-600">Afslut opgaven? Tiden stoppes, og opgaven bliver fakturaklar.</p>
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={() => setConfirming(false)} className="h-11 rounded-xl border border-slate-200 text-slate-600 font-semibold">Fortryd</button>
                            <button onClick={() => doAdvance("afslut")} disabled={busy === "afslut"} data-testid="workflow-confirm-afslut"
                                className="h-11 rounded-xl bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2">
                                {busy === "afslut" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Bekræft
                            </button>
                        </div>
                    </div>
                ) : (
                    <button onClick={() => next === "afslut" ? setConfirming(true) : doAdvance(next)} disabled={!!busy} data-testid="workflow-next-action"
                        className={`w-full h-12 rounded-xl text-white font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform ${next === "afslut" ? "bg-emerald-600" : "bg-blue-600"}`}>
                        {busy === next ? <Loader2 className="w-5 h-5 animate-spin" /> : NextIcon ? <NextIcon className="w-5 h-5" /> : null}
                        {ACTION_LABEL[next]}
                    </button>
                )
            ) : (
                <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-emerald-700" data-testid="workflow-done">
                    <FileCheck2 className="w-5 h-5" />
                    <div className="text-sm"><span className="font-bold">Fakturaklar.</span> Fakturagodkendelse og -afsendelse sker hos ejer/admin.</div>
                </div>
            )}

            {/* Arrival / call panel — most relevant while 'På vej' */}
            {(wf.stage === "paa_vej" || wf.stage === "modtaget") && (
                <ArrivalPanel jobId={jobId} customer={job.customer || {}} wf={wf} onChange={onChange} onRefresh={onRefresh} />
            )}

            {/* Time tracking */}
            {showTimes && (
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-3" data-testid="workflow-times">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-slate-400" />
                            <div><p className="text-[11px] uppercase tracking-wide text-slate-400">Køretid</p><p className="text-base font-bold tabular-nums text-slate-800" data-testid="drive-time">{fmt(drive)}</p></div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Timer className="w-4 h-4 text-slate-400" />
                            <div><p className="text-[11px] uppercase tracking-wide text-slate-400">Arbejdstid {wf.paused && <span className="text-amber-600">(pause)</span>}</p><p className="text-base font-bold tabular-nums text-slate-800" data-testid="work-time">{fmt(work)}</p></div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {wf.stage === "i_gang" && (
                            <button onClick={togglePause} disabled={busy === "pause"} data-testid="workflow-pause"
                                className="flex-1 h-9 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 flex items-center justify-center gap-1.5">
                                {busy === "pause" ? <Loader2 className="w-4 h-4 animate-spin" /> : wf.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                                {wf.paused ? "Genoptag" : "Pause"}
                            </button>
                        )}
                        <button onClick={() => { setDriveMin(Math.round((drive / 60) * 10) / 10); setWorkMin(Math.round((work / 60) * 10) / 10); setEditTime((v) => !v); }} data-testid="workflow-edit-time"
                            className="flex-1 h-9 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 flex items-center justify-center gap-1.5">
                            <PencilLine className="w-4 h-4" /> Ret tid
                        </button>
                    </div>
                    {editTime && (
                        <div className="space-y-2 pt-1" data-testid="workflow-time-edit">
                            <div className="grid grid-cols-2 gap-2">
                                <label className="text-xs text-slate-500">Køretid (min)
                                    <input type="number" min="0" value={driveMin} onChange={(e) => setDriveMin(Number(e.target.value))} data-testid="edit-drive-min"
                                        className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 text-sm" /></label>
                                <label className="text-xs text-slate-500">Arbejdstid (min)
                                    <input type="number" min="0" value={workMin} onChange={(e) => setWorkMin(Number(e.target.value))} data-testid="edit-work-min"
                                        className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 text-sm" /></label>
                            </div>
                            <button onClick={saveTime} disabled={busy === "time"} data-testid="workflow-save-time"
                                className="w-full h-9 rounded-lg bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-2">
                                {busy === "time" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Gem rettelse
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

function ArrivalPanel({ jobId, customer, wf, onChange, onRefresh }) {
    const api = useEmployeeApi();
    const [expected, setExpected] = useState(wf.arrival?.expected_at || "");
    const [lead, setLead] = useState(wf.arrival?.lead_minutes ?? 5);
    const method = "employee";
    const [note, setNote] = useState(wf.arrival?.note || "");
    const [saving, setSaving] = useState("");
    const phone = customer.phone || "";
    const notif = wf.arrival?.notification;
    const canSchedule = wf.stage === "paa_vej";

    const save = async () => {
        setSaving("save");
        try {
            const r = await api.setArrival(jobId, { expected_at: expected, lead_minutes: Number(lead) || 0, method, note });
            onChange?.(r.workflow);
            toast.success(r.workflow?.arrival?.notification?.status === "pending" ? "Intern påmindelse planlagt" : "Ankomstønske gemt");
        } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke gemme"); }
        finally { setSaving(""); }
    };
    const fireNow = async () => {
        setSaving("fire");
        try { const r = await api.notifyNow(jobId); onChange?.(r.workflow); onRefresh?.(); toast(r.fired ? "Intern påmindelse registreret" : r.status === "cancelled" ? "Varsling annulleret" : "Ingen ny påmindelse registreret"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke udløse"); }
        finally { setSaving(""); }
    };
    const fmtFire = (iso) => (iso ? new Date(iso).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Copenhagen" }) : "");

    return (
        <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 space-y-3" data-testid="arrival-panel">
            <h3 className="text-xs font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />Ankomstvarsling</h3>
            <div>
                <p className="font-semibold text-slate-900">{customer.name || "Kunde"}</p>
                {phone && <p className="text-sm text-slate-600">{phone}</p>}
                {customer.notes && <p className="text-sm text-slate-600 mt-1 italic">“{customer.notes}”</p>}
            </div>
            {phone && (
                <a href={`tel:${phone}`} data-testid="arrival-call" className="w-full h-11 rounded-xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform">
                    <Phone className="w-4 h-4" /> Ring kunden
                </a>
            )}
            <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">Forventet ankomst
                    <input value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="fx 11.15" data-testid="arrival-expected"
                        className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white" /></label>
                <label className="text-xs text-slate-500">Kundens varsel (min før)
                    <input type="number" min="0" value={lead} onChange={(e) => setLead(e.target.value)} data-testid="arrival-lead"
                        className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white" /></label>
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="arrival-method-picker">
                <button type="button" disabled data-testid="arrival-method-employee" aria-pressed={method === "employee"}
                    className={`h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 ${method === "employee" ? "border-2 border-blue-500 bg-white text-blue-700" : "border border-slate-200 bg-white text-slate-500"}`}>
                    <Phone className="w-4 h-4" /> Jeg ringer
                </button>
                <button type="button" disabled data-testid="arrival-method-ai" title="AI-ankomstopkald er ikke tilsluttet"
                    className="min-h-10 rounded-lg text-xs flex flex-col items-center justify-center border border-slate-200 text-slate-400">
                    <span className="flex items-center gap-1"><Bot className="w-3.5 h-3.5" /> AI ringer</span><span>Ikke tilsluttet</span>
                </button>
            </div>
            <button onClick={save} disabled={!!saving || !canSchedule} data-testid="arrival-save"
                className="w-full h-10 rounded-lg bg-white border border-blue-200 text-blue-700 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {saving === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />} Planlæg intern påmindelse
            </button>
            {!canSchedule && <p className="text-[11px] text-slate-500" data-testid="arrival-locked">Varsling planlægges når du er “På vej”.</p>}
            {notif && (
                <div className={`rounded-lg px-3 py-2 text-xs ${notif.status === "pending" ? "bg-amber-50 border border-amber-200 text-amber-800" : notif.status === "sent" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-slate-100 border border-slate-200 text-slate-600"}`} data-testid="arrival-notification" data-status={notif.status}>
                    {notif.status === "pending" && (
                        <>
                            <p className="font-semibold">{notif.method === "ai" ? "AI-opkald er ikke tilsluttet" : `Intern påmindelse planlagt kl. ${fmtFire(notif.fire_at)}`}</p>
                            <button onClick={fireNow} disabled={!!saving || notif.method === "ai"} data-testid="arrival-fire-now" className="mt-1.5 h-8 px-3 rounded-lg bg-amber-500 text-white font-semibold flex items-center gap-1.5 disabled:opacity-50">
                                {saving === "fire" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />} Vis påmindelse nu
                            </button>
                        </>
                    )}
                    {notif.status === "sent" && <p className="font-semibold">{notif.method === "ai" ? "Historisk simulering — intet opkald udført" : "Intern påmindelse registreret"} kl. {fmtFire(notif.sent_at)}</p>}
                    {notif.status === "cancelled" && <p className="font-semibold">Varsling annulleret — {notif.cancel_reason || "opgaven er ikke længere På vej"}</p>}
                </div>
            )}
        </div>
    );
}
